/**
 * PodLabelDialog — modale per modificare o rimuovere l'etichetta di un POD.
 *
 * Replica del mobile `LabelPodModal.js`:
 *  - input testo con max 50 char
 *  - bottone Salva/Aggiorna → updateEtichettaPod(pod, label)
 *  - link "Rimuovi etichetta" visibile solo se ce n'è già una → manda ""
 */

import { useEffect, useState, type FormEvent } from 'react'
import { Tag } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { useUpdateEtichettaPod } from '@/lib/graphql/hooks'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Codice cliente (per invalidare la query getCliente dopo update). */
  codiceCliente: string
  /** Codice POD da modificare. */
  codicePod: string
  currentLabel?: string | null
}

const MAX_LEN = 50

export function PodLabelDialog({
  open,
  onOpenChange,
  codiceCliente,
  codicePod,
  currentLabel,
}: Props) {
  const mutation = useUpdateEtichettaPod()

  const [label, setLabel] = useState(currentLabel ?? '')
  const [error, setError] = useState<string | null>(null)

  // Reset campo all'apertura
  useEffect(() => {
    if (!open) return
    setLabel(currentLabel ?? '')
    setError(null)
  }, [open, currentLabel])

  async function save(newLabel: string) {
    setError(null)
    try {
      await mutation.mutateAsync({
        codice_cliente: codiceCliente,
        codice_pod: codicePod,
        etichetta: newLabel || null,
      })
      onOpenChange(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore durante il salvataggio.')
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    save(label.trim())
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent>
        <DialogHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-secondary/15 text-secondary">
            <Tag className="h-5 w-5" />
          </div>
          <DialogTitle>
            {currentLabel ? 'Modifica etichetta' : 'Imposta etichetta'}
          </DialogTitle>
          <DialogDescription>
            Dai un nome a questa fornitura per riconoscerla facilmente.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="mt-2 flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pod-label">Nome fornitura</Label>
            <Input
              id="pod-label"
              type="text"
              placeholder="Es. Casa al mare"
              value={label}
              onChange={(e) => setLabel(e.target.value.slice(0, MAX_LEN))}
              maxLength={MAX_LEN}
              autoFocus
            />
            <p className="text-right text-xs text-muted-foreground">
              {label.length}/{MAX_LEN}
            </p>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Annulla
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <Spinner className="text-secondary-foreground" />
              ) : currentLabel ? (
                'Aggiorna'
              ) : (
                'Salva'
              )}
            </Button>
          </DialogFooter>

          {currentLabel && (
            <button
              type="button"
              onClick={() => save('')}
              disabled={mutation.isPending}
              className="self-center text-sm text-destructive underline-offset-2 hover:underline disabled:opacity-50"
            >
              Rimuovi etichetta
            </button>
          )}
        </form>
      </DialogContent>
    </Dialog>
  )
}
